/**
 * 性能监控工具集
 *
 * 提供 Web Vitals 监控、性能分析、资源加载追踪等功能
 */

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
}

type MetricCallback = (metric: PerformanceMetric) => void;

/**
 * Web Vitals 阈值配置
 */
const VITALS_THRESHOLDS = {
  // Largest Contentful Paint (LCP)
  LCP: { good: 2500, poor: 4000 },
  // First Input Delay (FID)
  FID: { good: 100, poor: 300 },
  // Cumulative Layout Shift (CLS)
  CLS: { good: 0.1, poor: 0.25 },
  // First Contentful Paint (FCP)
  FCP: { good: 1800, poor: 3000 },
  // Time to First Byte (TTFB)
  TTFB: { good: 800, poor: 1800 },
  // Interaction to Next Paint (INP)
  INP: { good: 200, poor: 500 },
};

/**
 * 获取指标评级
 */
function getRating(
  metricName: keyof typeof VITALS_THRESHOLDS,
  value: number
): 'good' | 'needs-improvement' | 'poor' {
  const { good, poor } = VITALS_THRESHOLDS[metricName];

  if (value <= good) return 'good';
  if (value <= poor) return 'needs-improvement';
  return 'poor';
}

/**
 * 监控 Largest Contentful Paint (LCP)
 * 最大内容绘制时间 - 衡量加载性能
 */
export function observeLCP(callback: MetricCallback) {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    return;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & {
        renderTime?: number;
        loadTime?: number;
      };

      const value = lastEntry.renderTime || lastEntry.loadTime || 0;

      callback({
        name: 'LCP',
        value,
        rating: getRating('LCP', value),
        timestamp: Date.now(),
      });
    });

    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch (error) {
    console.error('Error observing LCP:', error);
  }
}

/**
 * 监控 First Input Delay (FID)
 * 首次输入延迟 - 衡量交互性能
 */
export function observeFID(callback: MetricCallback) {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    return;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const firstInput = entries[0] as PerformanceEventTiming;

      const value = firstInput.processingStart - firstInput.startTime;

      callback({
        name: 'FID',
        value,
        rating: getRating('FID', value),
        timestamp: Date.now(),
      });
    });

    observer.observe({ type: 'first-input', buffered: true });
  } catch (error) {
    console.error('Error observing FID:', error);
  }
}

/**
 * 监控 Cumulative Layout Shift (CLS)
 * 累积布局偏移 - 衡量视觉稳定性
 */
export function observeCLS(callback: MetricCallback) {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    return;
  }

  let clsValue = 0;
  let sessionValue = 0;
  let sessionEntries: PerformanceEntry[] = [];

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        // 只统计无用户输入的偏移
        if (!entry.hadRecentInput) {
          const firstSessionEntry = sessionEntries[0];
          const lastSessionEntry = sessionEntries[sessionEntries.length - 1];

          // 如果条目与前一个条目的间隔小于 1 秒且
          // 与会话中第一个条目的间隔小于 5 秒，则
          // 将条目包含在当前会话中
          if (
            sessionValue &&
            entry.startTime - lastSessionEntry.startTime < 1000 &&
            entry.startTime - firstSessionEntry.startTime < 5000
          ) {
            sessionValue += entry.value;
            sessionEntries.push(entry);
          } else {
            sessionValue = entry.value;
            sessionEntries = [entry];
          }

          // 如果当前会话值大于当前 CLS 值，
          // 更新 CLS 及其相关条目
          if (sessionValue > clsValue) {
            clsValue = sessionValue;

            callback({
              name: 'CLS',
              value: clsValue,
              rating: getRating('CLS', clsValue),
              timestamp: Date.now(),
            });
          }
        }
      }
    });

    observer.observe({ type: 'layout-shift', buffered: true });
  } catch (error) {
    console.error('Error observing CLS:', error);
  }
}

/**
 * 监控 First Contentful Paint (FCP)
 * 首次内容绘制 - 衡量感知加载速度
 */
export function observeFCP(callback: MetricCallback) {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    return;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const fcpEntry = entries.find((entry) => entry.name === 'first-contentful-paint');

      if (fcpEntry) {
        const value = fcpEntry.startTime;

        callback({
          name: 'FCP',
          value,
          rating: getRating('FCP', value),
          timestamp: Date.now(),
        });
      }
    });

    observer.observe({ type: 'paint', buffered: true });
  } catch (error) {
    console.error('Error observing FCP:', error);
  }
}

/**
 * 监控 Time to First Byte (TTFB)
 * 首字节时间 - 衡量服务器响应速度
 */
export function observeTTFB(callback: MetricCallback) {
  if (typeof window === 'undefined' || !('performance' in window)) {
    return;
  }

  try {
    const navTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

    if (navTiming) {
      const value = navTiming.responseStart - navTiming.requestStart;

      callback({
        name: 'TTFB',
        value,
        rating: getRating('TTFB', value),
        timestamp: Date.now(),
      });
    }
  } catch (error) {
    console.error('Error observing TTFB:', error);
  }
}

/**
 * 监控所有 Web Vitals
 */
export function observeAllVitals(callback: MetricCallback) {
  observeLCP(callback);
  observeFID(callback);
  observeCLS(callback);
  observeFCP(callback);
  observeTTFB(callback);
}

/**
 * 页面加载性能分析
 */
export function analyzePageLoad() {
  if (typeof window === 'undefined' || !('performance' in window)) {
    return null;
  }

  const navTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

  if (!navTiming) return null;

  return {
    // DNS 查询时间
    dns: navTiming.domainLookupEnd - navTiming.domainLookupStart,
    // TCP 连接时间
    tcp: navTiming.connectEnd - navTiming.connectStart,
    // SSL 连接时间
    ssl: navTiming.secureConnectionStart > 0
      ? navTiming.connectEnd - navTiming.secureConnectionStart
      : 0,
    // 请求时间
    request: navTiming.responseStart - navTiming.requestStart,
    // 响应时间
    response: navTiming.responseEnd - navTiming.responseStart,
    // DOM 解析时间
    domParse: navTiming.domInteractive - navTiming.fetchStart,
    // DOM 内容加载完成时间
    domContentLoaded: navTiming.domContentLoadedEventEnd - navTiming.domContentLoadedEventStart,
    // 页面完全加载时间
    loaded: navTiming.loadEventEnd - navTiming.loadEventStart,
    // 总时间
    total: navTiming.loadEventEnd - navTiming.fetchStart,
  };
}

/**
 * 资源加载性能分析
 */
export function analyzeResourceLoad() {
  if (typeof window === 'undefined' || !('performance' in window)) {
    return [];
  }

  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];

  return resources.map((resource) => ({
    name: resource.name,
    type: resource.initiatorType,
    duration: resource.duration,
    size: resource.transferSize,
    // 缓存状态
    cached: resource.transferSize === 0 && resource.decodedBodySize > 0,
  }));
}

/**
 * 长任务监控
 * 检测阻塞主线程的长任务（> 50ms）
 */
export function observeLongTasks(callback: (tasks: any[]) => void) {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    return;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const longTasks = entries.map((entry: any) => ({
        name: entry.name,
        duration: entry.duration,
        startTime: entry.startTime,
        attribution: entry.attribution?.[0]?.name || 'unknown',
      }));

      callback(longTasks);
    });

    observer.observe({ type: 'longtask', buffered: true });
  } catch (error) {
    console.error('Error observing long tasks:', error);
  }
}

/**
 * 内存使用监控
 */
export function getMemoryUsage() {
  if (typeof window === 'undefined' || !('performance' in window)) {
    return null;
  }

  const memory = (performance as any).memory;

  if (!memory) return null;

  return {
    // 已使用内存（字节）
    usedJSHeapSize: memory.usedJSHeapSize,
    // 总分配内存（字节）
    totalJSHeapSize: memory.totalJSHeapSize,
    // 内存限制（字节）
    jsHeapSizeLimit: memory.jsHeapSizeLimit,
    // 使用率
    usageRate: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
  };
}

/**
 * 性能报告生成
 */
export function generatePerformanceReport() {
  const report: any = {
    timestamp: new Date().toISOString(),
    pageLoad: analyzePageLoad(),
    resources: analyzeResourceLoad(),
    memory: getMemoryUsage(),
  };

  console.group('📊 Performance Report');
  console.log('Page Load:', report.pageLoad);
  console.log('Resources:', report.resources);
  console.log('Memory:', report.memory);
  console.groupEnd();

  return report;
}
